import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Plus, X } from "lucide-react";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { glyphKey } from "@/shared/avatar/glyph";
import { createAgent } from "@/shared/api/agents";
import { inviteMember } from "@/shared/api/members";
import { patchSettings } from "@/shared/api/settings";
import { TEMPLATES_CATALOG } from "@/shared/lib/template-catalog";
import type { OnboardingState, Template } from "@/shared/types";

import { putOnboarding } from "./api/onboarding";
import { patchOrg } from "./api/org";
import { Step, NextButton, StepError, OnboardingShell, inputCls } from "./OnboardingFrame";
import { useSubmit } from "./hooks/useSubmit";
import type { OnboardingData, OnboardingResult } from "./types";

/* First-run setup — one question per screen, shown until it is finished.
   Ends by creating the first agent (`POST /agents`) from the picked template
   or the description, never by dismissing a form. The last screen is the only optional one; asking for
   invites before the workspace has anything in it is a toll booth.

   Progress lives on the server, not in this component: every Continue writes
   the answers so far through `PUT /settings/onboarding`, so closing the tab at
   step 3 reopens at step 3 with the first two answers already in the fields.
   The step index is the one to *resume* at, which is why each save sends the
   index of the step it is moving to. */

/* Free-text prompt → an agent name. Deliberately dumb: strips the common
   "I want an agent that…" preamble and title-cases what's left. The step shows
   the result before it is created, and it can be renamed in settings after. */
export function nameFromPrompt(prompt: string): string {
  const stripped = prompt
    .trim()
    .replace(/^(i (want|need) (an? )?agent (that|to|which) |help me |create an agent (that|to) |an agent (that|to) |agent (that|to) |something (that|to) )/i, "");
  const words = stripped
    .replace(/[^\w\s-]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3);
  if (!words.length) return "New Agent";
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LAST_STEP = 3;

/** `PATCH /settings` caps each name part at 50. */
const NAME_MAX = 50;

/** `PUT /settings/onboarding` rejects a `data` over 8KB. The brief is the only
    field a user can make arbitrarily long, so capping it is the whole guard. */
const PROMPT_MAX = 4000;

interface OnboardingProps {
  /** Server-held progress. Absent = a wizard that has never been started. */
  initial?: OnboardingState;
  /** Provision's default org name, the prefill for step 2. */
  defaultOrgName?: string;
  onDone: (result: OnboardingResult) => void;
}

export default function Onboarding({ initial, defaultOrgName, onDone }: OnboardingProps) {
  // `data` is opaque on the wire; this is the one place it is read as a shape.
  const saved = (initial?.data ?? {}) as OnboardingData;
  // Clamped: a `step` past the last screen would render nothing at all, and a
  // completed wizard is App's call to make, not this component's.
  const [step, setStep] = useState(Math.min(Math.max(initial?.step ?? 0, 0), LAST_STEP));
  const [name, setName] = useState(saved.name ?? "");
  const [org, setOrg] = useState(saved.org ?? defaultOrgName ?? "");
  const [template, setTemplate] = useState<Template | null>(
    () => TEMPLATES_CATALOG.find((t) => t.id === saved.template_id) ?? null
  );
  const [prompt, setPrompt] = useState(saved.prompt ?? "");
  const [invites, setInvites] = useState<string[]>(saved.invites?.length ? saved.invites : [""]);
  // Per-address, because "one of five bounced" has to say which one.
  const [inviteErrors, setInviteErrors] = useState<Record<number, string>>({});
  // Addresses the server already accepted. A retry after a partial failure must
  // not re-send them — the second invite for a live address is an error.
  const sent = useRef(new Set<string>());
  // The agent already created. A retry after a failed finish must not create
  // a second one.
  const created = useRef<string | null>(null);

  const { pending, error, submit } = useSubmit();

  const nameOk = name.trim().length > 0;
  const orgOk = org.trim().length > 0;
  const agentOk = !!template || prompt.trim().length > 0;

  const back = () => setStep((s) => Math.max(0, s - 1));

  /** Everything answered so far, in the shape the server stores. */
  const data = (): OnboardingData => ({
    name: name.trim(),
    org: org.trim(),
    template_id: template?.id ?? null,
    prompt: template ? "" : prompt.trim(),
    invites: invites.map((e) => e.trim()).filter((e) => EMAIL_RE.test(e)),
  });

  /** Save, then move. The order matters: `setStep` is unreachable if the save
      throws, which is what keeps a failed step on screen. */
  const advance = (to: number, before?: () => Promise<unknown>) =>
    submit(async () => {
      if (before) await before();
      await putOnboarding({ step: to, data: data() });
      setStep(to);
    });

  const submitName = () => {
    const [first, ...rest] = name.trim().split(/\s+/);
    const last = rest.join(" ");
    return advance(1, async () => {
      // 50 each is the server's limit. Checked here rather than truncated:
      // quietly shortening someone's name is worse than telling them.
      if (first.length > NAME_MAX || last.length > NAME_MAX)
        throw new Error(`First and last name are limited to ${NAME_MAX} characters each.`);
      await patchSettings({ first_name: first, last_name: last || null });
    });
  };

  const submitOrg = () => advance(2, () => patchOrg({ name: org.trim() }));

  // Only recorded here: the agent is created on finish, so going back to change
  // the choice never leaves a first attempt behind.
  const submitAgent = () => advance(3);

  const finish = (withInvites: boolean) =>
    submit(async () => {
      const d = data();
      if (withInvites) {
        const errs: Record<number, string> = {};
        // Sequential: five addresses at most, and a shared "one email per org"
        // constraint that reads better resolved one at a time than in a race.
        for (const [i, raw] of invites.entries()) {
          const email = raw.trim();
          if (!EMAIL_RE.test(email) || sent.current.has(email)) continue;
          try {
            await inviteMember(email);
            sent.current.add(email);
          } catch (e) {
            console.error(e); // the row shows the message; the console keeps the cause
            errs[i] = (e as Error)?.message || "Could not send";
          }
        }
        setInviteErrors(errs);
        // Partial failure keeps the screen: the user fixes the address and
        // presses again, and the ones that went out are not sent twice.
        if (Object.keys(errs).length) throw new Error("Some invites didn't go out.");
      }
      // Only what was chosen: a wizard resumed past the agent step with no
      // choice saved creates nothing, rather than a made-up "New Agent".
      if (!created.current && agentOk) {
        const body = template
          ? { name: template.name, instructions: template.instructions, hue: template.hue, seed: glyphKey(template.seed) }
          : { name: nameFromPrompt(prompt), instructions: prompt.trim() };
        created.current = (await createAgent(body)).id;
      }
      await putOnboarding({ step: LAST_STEP + 1, data: d, completed: true });
      onDone({ agentId: created.current });
    });

  // Deterministic avatar seed so the preview on step 1 doesn't re-roll while
  // the user types their name.
  const seedRef = useRef(`user-${Math.floor(Math.random() * 1e6)}`);

  const steps = [
    <Step
      index={0}
      total={4}
      eyebrow="Welcome"
      title="What should we call you?"
      subtitle="Your agents greet you by name. You can change it later in Settings."
      footer={<div><NextButton disabled={!nameOk || pending} onClick={submitName} /><StepError message={error} /></div>}
    >
      <div className="flex items-center gap-4">
        <PixelAvatar human seed={seedRef.current} hue={260} size={56} ring rounded={12} animate="alive" />
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && nameOk && submitName()}
          placeholder="Alex Morgan"
          className={inputCls}
        />
      </div>
    </Step>,

    <Step
      index={1}
      total={4}
      eyebrow="Workspace"
      title="What's your organization called?"
      subtitle="Names the workspace your agents, traces, and teammates live in."
      onBack={back}
      footer={<div><NextButton disabled={!orgOk || pending} onClick={submitOrg} /><StepError message={error} /></div>}
    >
      <input
        autoFocus
        value={org}
        onChange={(e) => setOrg(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && orgOk && submitOrg()}
        placeholder="Studio Co"
        className={inputCls}
      />
    </Step>,

    <Step
      index={2}
      total={4}
      eyebrow="First agent"
      title="What should your first agent do?"
      subtitle="Start from a template or describe what it should do. You can change its instructions any time."
      onBack={back}
      footer={
        <div>
          <NextButton disabled={!agentOk || pending} onClick={submitAgent}>
            {template ? `Set up ${template.name}` : prompt.trim() ? "Build it" : "Continue"}
          </NextButton>
          <StepError message={error} />
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {TEMPLATES_CATALOG.map((t) => {
          const picked = template?.id === t.id;
          return (
            <button
              key={t.id}
              onClick={() => {
                setTemplate(picked ? null : t);
                setPrompt("");
              }}
              className={`press text-left p-3 rounded-[10px] bg-[var(--surface)] ring-1 flex items-start gap-2.5 ${
                picked
                  ? "ring-[var(--text-primary)]"
                  : "ring-[var(--hairline)] hover:bg-[var(--accent-hover)]"
              }`}
            >
              <PixelAvatar seed={t.seed} hue={t.hue} size={28} rounded={8} />
              <span className="min-w-0">
                {/* Two to a row on a phone: a name wraps rather than losing its last word */}
                <span className="block break-words text-[12.5px] font-medium text-[var(--text-primary)]">
                  {t.name}
                </span>
                {/* line-clamp brings its own display; pairing it with `block`
                    would cancel the clamp and let rows drift out of step. */}
                <span className="mt-0.5 text-[11px] leading-[1.45] text-[var(--text-tertiary)] line-clamp-2">
                  {t.tagline}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <span className="flex-1 h-px bg-[var(--hairline)]" />
        <span className="text-[10.5px] text-[var(--text-quaternary)]">or describe it yourself</span>
        <span className="flex-1 h-px bg-[var(--hairline)]" />
      </div>

      <div>
        <textarea
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            if (e.target.value) setTemplate(null);
          }}
          rows={3}
          maxLength={PROMPT_MAX}
          placeholder="Chase unpaid invoices over email and tell me who's overdue every Monday"
          className="w-full resize-none px-3.5 py-3 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[13.5px] leading-[1.55] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]"
        />
        {prompt.trim() && (
          <p className="fade-in mt-2 flex items-center gap-1.5 text-[11px] text-[var(--text-tertiary)]">
            <Sparkles className="w-3 h-3" strokeWidth={1.75} />
            Creates <span className="text-[var(--text-primary)] font-medium">{nameFromPrompt(prompt)}</span> with this as its instructions.
          </p>
        )}
      </div>
    </Step>,

    <Step
      index={3}
      total={4}
      eyebrow="Optional"
      title="Bring your team in"
      subtitle="Teammates see the same agents, traces and evals. You can do this later from Settings → Members."
      onBack={back}
      footer={
        <div className="flex items-center gap-2">
          <NextButton disabled={pending} onClick={() => finish(true)}>
            {invites.some((e) => EMAIL_RE.test(e.trim())) ? "Send and finish" : "Finish setup"}
          </NextButton>
          <button
            onClick={() => finish(false)}
            disabled={pending}
            className="press h-11 px-4 rounded-[10px] text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
          >
            Skip
          </button>
          <StepError message={error} />
        </div>
      }
    >
      <div className="space-y-2">
        {invites.map((email, i) => (
          <div key={i}>
            <div className="flex items-center gap-2">
              <input
                autoFocus={i === 0}
                type="email"
                value={email}
                onChange={(e) =>
                  setInvites((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                }
                placeholder="teammate@company.com"
                className={inputCls}
              />
              {invites.length > 1 && (
                <button
                  onClick={() => setInvites((prev) => prev.filter((_, j) => j !== i))}
                  aria-label="Remove invite"
                  className="press shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              )}
            </div>
            {inviteErrors[i] && (
              <p className="fade-in mt-1 text-[11px] text-[var(--danger)]">{inviteErrors[i]}</p>
            )}
          </div>
        ))}
        {invites.length < 5 && (
          <button
            onClick={() => setInvites((prev) => [...prev, ""])}
            className="press flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <Plus className="w-3 h-3" strokeWidth={1.75} />
            Add another
          </button>
        )}
      </div>
    </Step>,
  ];

  // Esc never exits — there is nowhere to exit to before setup is done.
  useEffect(() => {
    const stop = (e: KeyboardEvent) => e.key === "Escape" && e.preventDefault();
    window.addEventListener("keydown", stop);
    return () => window.removeEventListener("keydown", stop);
  }, []);

  return <OnboardingShell>{steps[step]}</OnboardingShell>;
}
